/**
 * Generic builder for the `ml_lens` envelope that lets the ML Lens transformer
 * split a single GASv3 event into one metric per logical dimension (e.g. one
 * metric per product/connector).
 *
 * See: https://hello.atlassian.net/wiki/spaces/~93171358/pages/6820686403/
 *   GASv3+Events+Transformation+for+ML+Lens+Metrics+Schema
 *
 * This module is intentionally domain-agnostic: callers provide the namespace,
 * the metric-set base tags and the list of metrics. Surface-specific helpers
 * (e.g. Quick Find post-query, Full Page Search results) live alongside this
 * file and delegate to {@link buildMlLensEnvelope}.
 */

export type MlLensMetric = {
	key: string;
	type: 'number';
	value: number;
	tags: Record<string, string>;
};

export type MlLensMetricSet = {
	namespace: string;
	tags: Record<string, string>;
	metrics: MlLensMetric[];
};

export type MlLensEnvelope = {
	version: '1';
	metric_sets: MlLensMetricSet[];
};

export type BuildMlLensEnvelopeArgs = {
	/** Metric-set namespace, e.g. `rovo.quick_find` or `rovo.full_search_page`. */
	namespace: string;
	/** Base tags applied to the metric set, e.g. `{ page, task }`. */
	baseTags: Record<string, string>;
	/**
	 * The metrics for this metric set. Must be non-empty: an envelope carrying
	 * `"metrics":[]` invalidates the whole `ml_lens` payload downstream (the ML
	 * Lens transformer has no metric to split the event into), so when this is
	 * empty {@link buildMlLensEnvelope} returns `null` and callers must not
	 * attach `ml_lens` at all.
	 */
	metrics: MlLensMetric[];
	/**
	 * Current user id (Atlassian account id). Required for the envelope to be
	 * emitted: when it is `null`/`undefined`/empty, {@link buildMlLensEnvelope}
	 * returns `null` and callers must not attach `ml_lens` at all. When present
	 * it is added to the metric-set `tags` as `user_id`.
	 */
	userId?: string | null;
};

/**
 * Build a versioned `ml_lens` envelope with a single metric set.
 *
 * Returns `null` — meaning callers must omit the `ml_lens` tag entirely — when
 * either:
 *
 * - there is no `userId`. ML Lens metrics are keyed by `user_id`, so a userless
 *   envelope carries no analyzable signal; or
 * - `metrics` is empty. An envelope serialized with `"metrics":[]` invalidates
 *   the whole `ml_lens` payload for the transformer, so a metric-less envelope
 *   is worse than no envelope at all.
 *
 * In both cases emitting the tag would only add payload that downstream has to
 * discard, so we bail out before building it.
 */
export const buildMlLensEnvelope = ({
	namespace,
	baseTags,
	metrics,
	userId,
}: BuildMlLensEnvelopeArgs): MlLensEnvelope | null => {
	if (!userId) {
		return null;
	}

	if (metrics.length === 0) {
		return null;
	}

	return {
		version: '1',
		metric_sets: [
			{
				namespace,
				tags: { ...baseTags, user_id: userId },
				metrics,
			},
		],
	};
};
